import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";

// Contexts
import { AuthContext } from "../../context/AuthProvider";
import { BundleContext } from "../../context/BundleProvider";
import { ThemeContext } from "../../context/ThemeProvider";

// Components
import {
  DomainSelector,
  TestConfiguration,
  TestOptions,
  BuildSummary,
  PracticeTestDialog
} from "./components";
import CommonHeader from "../../components/common/CommonHeader";
import StatsGrid from "../../components/common/StatsGrid";
import { faWrench, faLayerGroup, faQuestionCircle, faClock, faChartLine } from "@fortawesome/free-solid-svg-icons";

// Constants
import { GET_APP_DOMAIN_LIST } from "../../../app_constant";
import { QUESTION_PRIORITY, SHOW_TOAST } from "../../constant/utils";
import { STRING, STRINGS } from "../../constant";

const CustomTestBuilder = () => {
  const navigate = useNavigate();
  const { isSubscribe, profile, community } = useContext(AuthContext);
  const { selectedCertificate } = useContext(BundleContext);
  const { theme } = useContext(ThemeContext);

  const APP_DOMAIN_LIST = GET_APP_DOMAIN_LIST(selectedCertificate);

  // State management
  const [selectedDomains, setSelectedDomains] = useState(APP_DOMAIN_LIST);
  const [testConfig, setTestConfig] = useState({
    numberOfQuestions: 5,
    timeAmount: 0,
    mode: 'questions' // 'questions' or 'time'
  });
  const [testOptions, setTestOptions] = useState({
    showAnswer: false,
    priority: STRING.default
  });
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [testDetails, setTestDetails] = useState(null);

  // Calculate domain scores
  const getDomainScore = (domainId) => {
    const scores = profile?.readiness_scores?.topic_progress || [];
    const topic = scores.find(s => s.domain_id === domainId);
    if (topic) {
      const score = parseInt(topic.readiness_score || 0);
      return {
        score,
        color: score >= 70 ? theme?.primary?.green?.[500] : 
               score >= 40 ? theme?.primary?.yellow?.[500] : 
               theme?.primary?.red?.[500]
      };
    }
    return { score: 0, color: theme?.primary?.red?.[500] };
  };

  // Handle domain selection
  const handleDomainToggle = (domain) => {
    setSelectedDomains(prev => {
      const exists = prev.some(d => d.id === domain.id);
      if (exists) {
        return prev.filter(d => d.id !== domain.id);
      }
      return [...prev, domain];
    });
  };

  const handleSelectAll = (selectAll) => {
    setSelectedDomains(selectAll ? APP_DOMAIN_LIST : []);
  };

  // Handle configuration changes
  const handleConfigChange = (config) => {
    setTestConfig(config);
  };

  const handleOptionsChange = (options) => {
    setTestOptions(options);
  };

  // Build test
  const handleBuildTest = () => {
    // Validation
    if (selectedDomains.length === 0) {
      SHOW_TOAST("Please select at least one domain");
      return;
    }

    // Check subscription
    const needsSubscription = !isSubscribe && 
      (testConfig.numberOfQuestions > 5 || testConfig.timeAmount > 0);

    if (needsSubscription) {
      navigate("/chooseplan");
      return;
    }

    // Calculate test time
    const questionTime = profile?.time_per_question || 
                        community?.total_time_per_question || 
                        90;

    const testTime = testConfig.mode === 'time' 
      ? testConfig.timeAmount * 60 // Convert minutes to seconds
      : questionTime * testConfig.numberOfQuestions;

    // Build test details
    const test = {
      test_name: "Custom Test",
      num_questions: testConfig.mode === 'questions' ? testConfig.numberOfQuestions : null,
      test_time: testTime,
      total_time: testConfig.mode === 'time' ? testConfig.timeAmount : 0,
      topic_ids: selectedDomains.map(d => d.id),
      domain_names: selectedDomains.map(d => d.domain_name),
      is_custom_test: true,
      is_show_answer: testOptions.showAnswer,
      priority: testOptions.priority,
      mode: testConfig.mode
    };

    setTestDetails(test);
    setIsDialogOpen(true);
  };

  // Dialog handlers
  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setTestDetails(null);
  };

  const handleTestStart = () => {
    localStorage.setItem("testStart", JSON.stringify(true));
    setIsDialogOpen(false);
    navigate("/test", { state: testDetails });
  };

  const handleChoosePlan = () => {
    setIsDialogOpen(false);
    navigate("/chooseplan");
  };

  // Calculate summary stats
   const getSummaryStats = () => {
    const totalQuestions = selectedDomains.reduce((sum, domain) => {
      // You might want to get actual question count per domain
      // For now, using a placeholder
      return sum + 50; // Replace with actual count
    }, 0);

    const avgScore = selectedDomains.length > 0
      ? Math.round(
          selectedDomains.reduce((sum, domain) => {
            const { score } = getDomainScore(domain.id);
            return sum + score;
          }, 0) / selectedDomains.length
        )
      : 0;

    return {
      domainsSelected: selectedDomains.length,
      totalDomains: APP_DOMAIN_LIST.length,
      questionsAvailable: totalQuestions,
      avgDomainScore: avgScore
    };
  };

  const summaryStats = getSummaryStats();

  // Calculate stats for StatsGrid
  /* const calculateStats = () => {
    const statsData = [
      {
        icon: faLayerGroup,
        stat: `${summaryStats.domainsSelected}/${summaryStats.totalDomains}`,
        label: 'Domains Selected',
        color: theme?.primary?.orange?.[500],
        iconType: 'fontawesome'
      },
      {
        icon: faQuestionCircle,
        stat: summaryStats.questionsAvailable.toLocaleString(),
        label: 'Questions Available',
        color: theme?.primary?.orange?.[500],
        iconType: 'fontawesome'
      },
      {
        icon: faClock,
        stat: testConfig.mode === 'questions' 
          ? `${testConfig.numberOfQuestions} Qs`
          : `${testConfig.timeAmount} Min`,
        label: 'Test Duration',
        color: theme?.primary?.orange?.[500],
        iconType: 'fontawesome'
      },
      {
        icon: faChartLine,
        stat: `${summaryStats.avgDomainScore}%`,
        label: 'Avg Domain Score',
        color: theme?.primary?.orange?.[500],
        iconType: 'fontawesome'
      },
    ];

    return statsData;
  };

  const stats = calculateStats();  */

  return (
    <div className="min-h-screen transition-colors duration-300" 
         style={{ background: theme?.background?.main }}>
      <div>
        {/* Header */}
        <CommonHeader
          title="Custom Test Builder"
          icon={faWrench}
          subtitle="Build your personalized practice test with selected domains and preferences"
          quote="The best way to predict your future is to create it."
          onBack={() => navigate("/home")}
          theme={theme}
          showBackButton={true}
        />
        
        {/* Stats Grid */}
        {/* <div className="pb-4">
          <StatsGrid stats={stats} theme={theme} />
        </div> */}
        
        {/* Main Content */}
        <div className="space-y-6">
          {/* Domain Selection */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <DomainSelector
            domains={APP_DOMAIN_LIST}
            selectedDomains={selectedDomains}
            onDomainToggle={handleDomainToggle}
            onSelectAll={handleSelectAll}
            getDomainScore={getDomainScore}
            theme={theme}
          />
          <TestConfiguration
              config={testConfig}
              onChange={handleConfigChange}
              isSubscribed={isSubscribe}
              theme={theme}
            />
          </div>
          
          {/* Configuration Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            
              <TestOptions
                options={testOptions}
                onChange={handleOptionsChange}
                priorityOptions={QUESTION_PRIORITY}
                theme={theme}
              />
              
              <BuildSummary
                stats={summaryStats}
                config={testConfig}
                options={testOptions}
                onBuildTest={handleBuildTest}
                isSubscribed={isSubscribe}
                theme={theme}
              />
            
          </div>
        </div>
        
        {/* Practice Test Dialog */}
        {testDetails && (
          <PracticeTestDialog
            isOpen={isDialogOpen}
            onClose={handleDialogClose}
            onStart={handleTestStart}
            onChoosePlan={handleChoosePlan}
            testDetails={testDetails}
            isSubscribe={isSubscribe}
            theme={theme}
          />
        )}
      </div>
    </div>
  );
};

export default CustomTestBuilder;